import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AccountReceivable, Workflow, OrgStripeProduct } from '@/api';
import { AccountReceivablesService, WorkflowsService, OrganizationService } from '@/api';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';
import { Input } from '@/components/ui/input';

interface TestNodeDrawerProps {
  open: boolean;
  onClose: () => void;
  node: FormattedNode;
  workflow: Workflow | undefined;
}

export const TestNodeDrawer = ({ open, onClose, node, workflow }: TestNodeDrawerProps) => {
  const [accounts, setAccounts] = useState<AccountReceivable[]>([]);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [stripeProducts, setStripeProducts] = useState<OrgStripeProduct[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      fetchAccounts();
      if (workflow?.organizationId) {
        fetchStripeProducts(workflow.organizationId);
      }
    } else {
      setAccounts([]);
      setSelectedAccountId(null);
      setStripeProducts([]);
    }
  }, [open, workflow?.organizationId]);

  const fetchStripeProducts = async (orgId: string) => {
    try {
      const products = await OrganizationService.getStripeProducts(orgId);
      setStripeProducts(products);
    } catch (error) {
      console.error('Failed to fetch Stripe products:', error);
    }
  };

  const fetchAccounts = async () => {
    if (!workflow?.executionData?.portfolioIds?.length) return;
    
    setLoading(true);
    try {
      const portfolioIds = [...new Set(workflow.executionData?.portfolioIds)];
      const accountsPromises = portfolioIds.map(portfolioId =>
        AccountReceivablesService.getAllAccountReceivables(portfolioId)
      );
      
      const accountResults = await Promise.all(accountsPromises);
      const allAccounts = accountResults.flat();
      const uniqueAccounts = [...new Set(allAccounts.map(a => a.id))]
        .map(id => allAccounts.find(a => a.id === id)!);
      
      setAccounts(uniqueAccounts);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!selectedAccountId || !workflow) return;
    
    setTestLoading(true);
    toast.info('Running test on Node..');
    try {
      const response = await WorkflowsService.testNode(workflow.id, node.id, {
        accountReceivableId: selectedAccountId,
      });

      if (!response.success) {
        throw new Error('Test failed');
      }
      toast.success('Test completed successfully');
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const email = account.customer?.contactEmail?.toLowerCase() || '';
    const stripeProduct = stripeProducts.find(p => p.stripeProductId === account.stripeProductId);
    const planName = (stripeProduct?.name || 'N/A').toLowerCase();
    const query = searchQuery.toLowerCase();
    
    return email.includes(query) || planName.includes(query);
  });

  return (
    <div className={`absolute bottom-16 right-6 min-h-[160px] min-w-[360px] rounded-lg bg-white p-4 pl-6 pr-6 shadow-lg ${!open && 'hidden'} z-10`}>
      <div className="flex justify-between">
        <div className="flex items-center">
          <p className="text-sm font-semibold tracking-tight">
            Test Node
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {accounts.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-3">Accounts:</h3>
          
          <div className="relative mb-3">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or plan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {filteredAccounts.map(account => {
              const stripeProduct = stripeProducts.find(p => p.stripeProductId === account.stripeProductId);
              return (
                <div key={account.id} className="flex items-center">
                  <Checkbox
                    checked={selectedAccountId === account.id}
                    onCheckedChange={() => setSelectedAccountId(account.id)}
                  />
                  <label className="ml-2 text-sm">
                    {account.customer?.contactEmail} - {stripeProduct?.name || 'N/A'}
                  </label>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleTest}
              disabled={!selectedAccountId || testLoading || !workflow}
            >
              Run Test
            </Button>
          </div>
        </div>
      )}

      {accounts.length === 0 && (
        <div className="mt-4">
          <Button
            onClick={fetchAccounts}
            disabled={loading || !workflow}
          >
            {loading ? 'Loading...' : 'Load Accounts'}
          </Button>
        </div>
      )}
    </div>
  );
}; 